import { useState } from 'react';
import { useParams } from 'react-router-dom';

import Text from '@ui/text/text/text';
import Icon from '@ui/icons/icon';
import TooltipPortal from '@ui/tooltip-portal/tooltip-portal';
import { ICONS } from '@ui/icons/types';

import {
  getIsSettingsUpdating,
  getWorkingProjectSessions,
  getWorkingProjectUuid,
  selectProjectSettings,
} from '@/store/slices/project/selectors';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectShowCalendar } from '@/store/slices/settings/selectors';
import { saveWorkingProjectSessionByField, updateProjectSettings } from '@/store/slices/project';

import { EventSettings } from '@/types/projects.types';

import { ICalendarDetailMetadata } from '../../settings.types';
import SettingsEditPanel from '../../controls/settings-edit-panel';
import CalendarDetail from './calendar-detail';
import SettingsToggle from '../../controls/settings-toggle';

const CalendarDetailWidget = () => {
  const isSettingsUpdating = useAppSelector(getIsSettingsUpdating);
  const showAddToCalendar = useAppSelector(selectShowCalendar);
  const settings = useAppSelector(selectProjectSettings);
  const projectUuid = useAppSelector(getWorkingProjectUuid);
  const sessions = useAppSelector(getWorkingProjectSessions);

  const params = useParams<{ projectUuid: string; language: string }>();
  const language = params.language || 'en';

  const [editingSessionUuid, setEditingSessionUuid] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [metadata, setMetadata] = useState<{ [key: string]: ICalendarDetailMetadata }>({});

  const dispatch = useAppDispatch();

  const handleSave = (sessionUuid: string) => {
    if (Object.keys(metadata).length === 0) {
      setEditingSessionUuid(null);
      return;
    }

    const session = sessions?.find((s) => s.uuid === sessionUuid);
    if (session) {
      const calendar_settings = {
        ...session.calendar_settings,
        content: {
          ...session.calendar_settings?.content,
          [language]: {
            ...session.calendar_settings?.content?.[language],
            ...(metadata[session.uuid].name && { title: metadata[session.uuid].name }),
            ...(metadata[session.uuid].description && { description: metadata[session.uuid].description }),
          },
        },
      };

      if (calendar_settings) {
        dispatch(
          saveWorkingProjectSessionByField({
            uuid: sessionUuid,
            calendar_settings,
          })
        );
      }
    }

    setEditingSessionUuid(null);
    setMetadata({});
  };

  const handleMetadataChange = (sessionUuid: string, updatedData: ICalendarDetailMetadata) => {
    setMetadata((prevChanges) => ({
      ...prevChanges,
      [sessionUuid]: {
        ...(prevChanges[sessionUuid] || {}),
        ...updatedData,
      },
    }));
  };

  const handleCancel = () => {
    setMetadata({});
    setEditingSessionUuid(null);
  };

  const handleCalendarToggle = async (val: string, on: boolean) => {
    if (settings && !isSettingsUpdating) {
      setIsLoading(true);
      const projectSettings: EventSettings = {
        ...settings,
        show_add_to_cal_btn: on,
      };
      if (projectUuid) {
        await dispatch(
          updateProjectSettings({
            event_uuid: projectUuid,
            settings: projectSettings,
          })
        );
      }
      setIsLoading(false);
    }
  };

  return (
    <div className="flex-column gap-8">
      <div className="flex gap-4">
        <Text className="H5" color="var(--gray-100)">
          Calendar Details
        </Text>
        <TooltipPortal
          childrenWrapperClass="flex"
          tooltipContent={<Text color="var(--gray-true-dark-100)">Customize your calendar content</Text>}
        >
          <Icon name={ICONS.INFO_CIRCLE} size={12} color="var(--gray-500)" />
        </TooltipPortal>
      </div>
      <SettingsToggle
        label="Add to calendar"
        value={showAddToCalendar}
        sKey=""
        onToggle={handleCalendarToggle}
        loading={isLoading}
      />

      {sessions?.map((session) => (
        <SettingsEditPanel
          isEditing={editingSessionUuid === session.uuid}
          onCancel={handleCancel}
          onSave={() => handleSave(session.uuid)}
          key={session.uuid}
        >
          <CalendarDetail
            isEditing={editingSessionUuid === session.uuid}
            setIsEditing={() => setEditingSessionUuid(session.uuid)}
            session={session}
            metadata={metadata[session.uuid] || {}}
            setMetadata={(data) => handleMetadataChange(session.uuid, data)}
          />
        </SettingsEditPanel>
      ))}
    </div>
  );
};

export default CalendarDetailWidget;
